import { iconIndex } from 'api/algolia';
import { SearchApiResponse } from 'hooks/usePaginatedIcon/usePaginatedIconSearch';
import { restrictSearchableAttributes } from 'hooks/usePaginatedIcon/usePaginatedIconSearch/usePaginatedIconSearch.config';
import {
  Icons8IconsApiResponse,
  Icons8IllustrationParams,
  Icons8IllustrationResponse,
  Icons8Params,
  fetchE2Icons8Api,
  listIcons8Illustrations,
} from 'libs/e2api/icons8';
import { GATED_ICONS8_PLATFORMS } from 'modules/Editor/components/WidgetMenu/SideMenu/IconWidgetMenu/DefaultIconView/iconPackData';
import { UserTypes } from 'modules/User';

import {
  hasMoreIcons8IllustrationIcons,
  parseAlgoliaIcons,
  parseIcons8Illustrations,
} from '../common/usePaginatedIcon.helpers';
import { IconSearchData } from '../common/usePaginatedIcon.types';
import { IconPackResponseTypeMap, IconPackState, IconPackTypes } from './usePaginatedIconPack.types';

const KEY_SEPARATOR = '/_';

const DEFAULT_HITS_PER_PAGE_PACK = 36;

/**
 * Fetch icons from Algolia
 * @param key
 * @returns
 */
const fetchAlgoliaIconPackData = (key: string) => {
  const { id, page } = parseKey(key);

  const facetFilters = ['published:-0', `iconPacks:${id}`];
  const hitsPerPage = DEFAULT_HITS_PER_PAGE_PACK;

  return iconIndex
    .search('', { hitsPerPage, facetFilters, restrictSearchableAttributes, page: +page })
    .then((content) => content);
};

/**
 * Fetch icons from Icons8
 * @param key
 * @param token
 * @returns
 */
const fetchIcons8IconPackData = (key: string, token: string) => {
  const { style: platform, category, page } = parseKey(key);

  const icons8IconsSearchParams: Icons8Params = {
    platform,
    category: category || '',
    limit: DEFAULT_HITS_PER_PAGE_PACK,
    offset: +page * DEFAULT_HITS_PER_PAGE_PACK,
  };

  return fetchE2Icons8Api(icons8IconsSearchParams, token).then((content) => content);
};

/**
 * Fetch icons8 illustrations
 * @param key
 * @param token
 * @returns
 */
const fetchIcons8IllustrationIconPackData = async (key: string, token: string) => {
  const { style, category, page } = parseKey(key);
  const icons8IconsSearchParams: Icons8IllustrationParams = {
    style,
    categories: category || '',
    perPage: DEFAULT_HITS_PER_PAGE_PACK,
    page: +page + 1,
  };

  return listIcons8Illustrations(icons8IconsSearchParams, token).then((content) => content);
};

/**
 * Fetch function map for different icon pack types
 */
export const FETCH_FN_MAP: Record<IconPackTypes, (key: string, token: string) => Promise<any>> = {
  algolia: fetchAlgoliaIconPackData,
  icons8: fetchIcons8IconPackData,
  icons8Illustration: fetchIcons8IllustrationIconPackData,
};

/**
 * Flatten Icons8 responses
 *
 * @param data
 * @returns
 */
export const parseIcons8IconsApi = (data: Icons8IconsApiResponse[]) => {
  if (!data) return [];
  const icons: { [key: string]: boolean } = {};

  return data
    .filter(Boolean)
    .flatMap((d) => d.icons)
    .filter((icon) => {
      if (!icon) return false;

      if (!icons[icon.id]) {
        icons[icon.id] = true;
        return true;
      }

      return false;
    });
};

/**
 * Parses icon pack fetch responses into IconSearchData[]
 * Note: a pack will only contain icons of the same type (i.e. all icons will either be from algolia, icons8, icons8 illustrations)
 *
 * @param type Type of icon pack that is being parsed
 * @param data Icon data for the pack
 * @returns parsed icon search data
 */
export function parseIcons<T extends IconPackTypes>(type: T, data?: IconPackResponseTypeMap[T][]): IconSearchData[] {
  if (!data) return [];

  let icons: IconSearchData[] = [];
  if (type === 'algolia') {
    icons = parseAlgoliaIcons(data as SearchApiResponse[]).map((icon) => ({
      id: icon.id,
      name: icon.name,
      permission: icon.type,
      isIllustration: icon.illustration === 1,
      isMono: icon.attr_style === 'mono',
    }));
  } else if (type === 'icons8') {
    icons = parseIcons8IconsApi(data as Icons8IconsApiResponse[]).map(({ id, name, platform, commonName }) => {
      let permission = 'default';
      if (GATED_ICONS8_PLATFORMS[UserTypes.Premium].includes(platform)) {
        permission = 'premium';
      }
      return {
        id: id,
        name: name,
        isMono: false,
        permission: permission as 'default' | 'premium',
        isIllustration: false,
        iconPath: `${platform}/${commonName}.svg`,
      };
    });
  } else if (type === 'icons8Illustration') {
    icons = parseIcons8Illustrations(data as Icons8IllustrationResponse[]).map(({ id, urls }) => ({
      id,
      name: id,
      isMono: false,

      // No gating for icons8
      permission: 'default' as const,

      isIllustration: true,
      iconIllustrationThumbnailUrl: urls.thumb.url,
    }));
  }

  return icons;
}

/**
 * Generate key for SWR immutable library
 * Will have format
 *
 * ICONPACK/_<type>::<id>/_<page>/_<style>/_<category>
 *
 * Example:
 * - ICONPACK/_ALGOLIA::1/_1 (algolia doesnt have style or category)
 * - ICONPACK/_ICONS8::1/_1/_parakeet/_business
 * - ICONPACK/_ICONS8ILLUSTRATION::1/_1/_transistor/_people
 *
 * @param page page number
 * @param iconPack icon pack data
 * @param type
 * @returns
 */
export const generateKey = (page: number, iconPack: IconPackState, type: string) => {
  let key = ['ICONPACK', type.toUpperCase()].join(KEY_SEPARATOR);

  let infoStr = [iconPack.id, page, iconPack.style, iconPack.category]
    .filter((str) => str !== undefined)
    .join(KEY_SEPARATOR);
  return `${key}::${infoStr}`;
};

/**
 * Parse key generated by generateKey
 *
 * @param key
 * @returns
 */
export const parseKey = (
  key: string,
): {
  id: number;
  page: string;
  style: string;
  category?: string;
} => {
  const [, infoStr] = key.split('::');
  const [id, page, style, category] = infoStr.split(KEY_SEPARATOR);
  return {
    id: parseInt(id),
    page,
    style,
    ...(category && { category }),
  };
};

/**
 * Check if there are more icons to load based on the last search response received
 *
 * @param type
 * @param currentPage current page from swr lib
 * @param lastResponse last response received from the fetch function
 * @returns boolean TRUE if there are more icons available, false otherwise
 */
export function checkHasMore<T extends IconPackTypes>(
  type: T,
  currentPage: number,
  lastResponse?: IconPackResponseTypeMap[T],
): boolean {
  if (!lastResponse) return false;

  let hasMore = false;
  if (type === 'algolia') {
    const algoliaResponse = lastResponse as SearchApiResponse;
    hasMore = algoliaResponse?.nbPages - 1 > algoliaResponse?.page;
  } else if (type === 'icons8') {
    const icons8Response = lastResponse as Icons8IconsApiResponse;
    hasMore = hasMoreIcons8IconsApi(icons8Response);
  } else if (type === 'icons8Illustration') {
    const icons8IllustrationResponse = lastResponse as Icons8IllustrationResponse;
    hasMore = hasMoreIcons8IllustrationIcons(icons8IllustrationResponse, currentPage, DEFAULT_HITS_PER_PAGE_PACK);
  }

  return hasMore;
}

/**
 * Check if there are more icons8 icons to load based on the last search response received
 *
 * @param lastIcons8Response The last Icons8 search response
 * @returns boolean
 */
export const hasMoreIcons8IconsApi = (lastIcons8Response: Icons8IconsApiResponse) => {
  if (
    !lastIcons8Response ||
    !lastIcons8Response.parameters ||
    !lastIcons8Response.parameters.countAll ||
    typeof lastIcons8Response.parameters.offset !== 'number'
  )
    return false;

  const totalNumPages = Math.ceil(lastIcons8Response.parameters.countAll / DEFAULT_HITS_PER_PAGE_PACK);
  const lastPage = Math.floor(lastIcons8Response.parameters.offset / DEFAULT_HITS_PER_PAGE_PACK);

  return lastPage < totalNumPages - 1;
};
